import React from "react";
import { Link } from "react-router-dom";
import { t } from "./utils/i18n";

const LEGAL_EFFECTIVE_DATE = "2026-10-23";

// The page fill follows the day/night scene (src/scene/brandSurfaces.css).
const pageStyle = {
  minHeight: "100vh",
  background: "var(--brand-page)",
  color: "#e2e8f0",
  padding: "3rem 1rem",
};

const shellStyle = {
  width: "min(900px, 100%)",
  margin: "0 auto",
  lineHeight: 1.75,
};

const navStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  marginBottom: "2.5rem",
};

const linkStyle = {
  color: "#7dd3fc",
  fontWeight: 700,
  textDecoration: "none",
};

const sectionStyle = {
  borderTop: "1px solid rgba(148,163,184,0.22)",
  paddingTop: "1.25rem",
  marginTop: "1.5rem",
};

function LegalShell({ title, children }) {
  return (
    <div style={pageStyle}>
      <main style={shellStyle}>
        <nav style={navStyle}>
          <Link to="/" style={linkStyle}>jolymei.com</Link>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Link to="/privacy" style={linkStyle}>{t("legal.privacy")}</Link>
            <Link to="/terms" style={linkStyle}>{t("legal.terms")}</Link>
          </div>
        </nav>
        <p style={{ color: "#94a3b8", fontWeight: 700, marginBottom: "0.35rem" }}>
          {t("legal.effective_date")} {LEGAL_EFFECTIVE_DATE}
        </p>
        <h1 style={{ margin: 0, color: "#f8fafc", fontSize: "clamp(2rem, 5vw, 3.4rem)" }}>
          {title}
        </h1>
        <div style={{ marginTop: "2rem" }}>{children}</div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={sectionStyle}>
      <h2 style={{ color: "#f8fafc", fontSize: "1.25rem", marginBottom: "0.6rem" }}>{title}</h2>
      {children}
    </section>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title={t("legal.privacy_title")}>
      <p>
        JolyMei is a personal, invite-only AI creator toolbox. This policy explains what data is
        collected when you create an account or use the tools, and how that data is used.
      </p>
      <Section title="1. Data we collect">
        <p>
          We may collect your email address, display alias, salted password hash, email verification
          status, login/session records, account timestamps, AI prompts, conversation history,
          uploaded reference images, generated images, saved spell relationships, public evolution
          directions for spell variations, Sigil document sigils you save, scrolls you create or edit
          and their version history, answers and material you submit to a sigil, temporary generation
          job status, bookmarks, quota usage, and basic technical logs needed to keep the service
          secure and working.
        </p>
        <p>
          Because access is by invitation, an email address may be added to the invitation list
          before its owner signs up, together with an optional note and access tier. We also record
          which invitation admitted an account, and keep a hashed one-time token while a password
          reset link is outstanding.
        </p>
      </Section>
      <Section title="2. How we use data">
        <p>
          We use this data to check invitations, create and secure accounts, verify email addresses,
          email password reset links, keep you signed in,
          provide the Grimoire and Sigil tools, generate images and documents, restore in-progress generation tasks, save
          creative workflows, show spell families, public variation notes and public sigils, apply usage limits,
          troubleshoot errors, prevent abuse, and respond to account or deletion requests.
        </p>
      </Section>
      <Section title="3. Third-party services">
        <p>
          JolyMei may process data through service providers such as MongoDB, Resend, OpenAI, Google
          Gemini, Hugging Face, Microsoft Azure Translator, Cloudflare R2 or compatible object
          storage, and Google Cloud Storage. Prompts, uploaded images, generated images, Sigil answers,
          material and scroll text, translated display text, and related metadata may be sent to these
          services only as needed to provide the requested feature.
        </p>
      </Section>
      <Section title="4. Cookies and local storage">
        <p>
          We use necessary cookies for login and security. The site may also store interface
          preferences, such as language and the last-used module, in your browser storage. Password
          resets end existing signed-in sessions.
        </p>
      </Section>
      <Section title="5. Retention">
        <p>
          Conversation records are scheduled to be removed after roughly 60 days of inactivity.
          Unsaved temporary generation job records are intended for short-term recovery and may expire
          after roughly 24 hours. Material pasted into a Sigil imprint stays only with its temporary job
          and is not stored with a saved scroll. Saved scrolls and their recent versions are kept until
          you delete them. Account records are kept while your account remains active. Uploaded or
          generated image files may remain in object storage unless removed through the app or by
          request. Password reset links expire after one hour. Invitation entries are kept until they
          are revoked or no longer needed; an invited person who does not want to join can ask for
          their entry to be removed.
        </p>
      </Section>
      <Section title="6. Your choices">
        <p>
          You can contact us to request access, correction, account deletion, or deletion of stored
          content where reasonably possible. Some records may be retained when needed for security,
          abuse prevention, or legal compliance.
        </p>
      </Section>
      <Section title="7. Contact">
        <p>
          For privacy questions or deletion requests, contact{" "}
          <a href={`mailto:${process.env.REACT_APP_EMAIL || ""}`} style={linkStyle}>
            {process.env.REACT_APP_EMAIL || "the site owner"}
          </a>.
        </p>
      </Section>
      <Section title="8. Changes to this policy">
        <p>
          This policy should be updated when JolyMei adds features that change what data is
          collected, how data is used, which third-party services process data, or how long data is
          kept. The effective date above identifies the current published version.
        </p>
      </Section>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title={t("legal.terms_title")}>
      <p>
        These terms apply when you access JolyMei. By creating an account or using the service, you
        agree to use it responsibly and within the limits below.
      </p>
      <Section title="1. Personal invite-only service">
        <p>
          JolyMei is provided as a personal project for invited users. Access may be limited,
          changed, paused, or discontinued at any time.
        </p>
      </Section>
      <Section title="2. Account responsibility">
        <p>
          You are responsible for keeping your login details secure and for activity under your
          account. Do not share invite codes or attempt to access another person's account.
        </p>
      </Section>
      <Section title="3. User content">
        <p>
          You are responsible for prompts, text, images, files, and other content you submit. Do not
          upload or generate illegal content, private information about others, infringing material,
          abusive content, or content you do not have the right to use.
        </p>
        <p>
          If you save a spell variation, its evolution direction may be shown to other users who can
          view that spell family. Do not put private or sensitive information in public variation
          notes.
        </p>
        <p>
          Sigils you save in Sigil are public: other users can see and use them, along with your
          display alias. Scrolls stay private to your account. Do not put private or sensitive
          information in a sigil.
        </p>
      </Section>
      <Section title="4. AI output">
        <p>
          AI output may be inaccurate, incomplete, offensive, or unsuitable for your intended use.
          You are responsible for reviewing output before relying on, publishing, or reusing it.
        </p>
      </Section>
      <Section title="5. Usage limits and enforcement">
        <p>
          We may apply quotas, rate limits, feature restrictions, content removal, account suspension,
          or account deletion to protect the service, control costs, or respond to misuse.
        </p>
      </Section>
      <Section title="6. No warranty">
        <p>
          The service is provided as is, without guarantees of availability, accuracy, security, or
          fitness for a particular purpose. Use it at your own discretion.
        </p>
      </Section>
      <Section title="7. Privacy">
        <p>
          Use of the service is also governed by the{" "}
          <Link to="/privacy" style={linkStyle}>{t("legal.privacy")}</Link>.
        </p>
      </Section>
      <Section title="8. Changes to these terms">
        <p>
          These terms should be updated when JolyMei adds or changes account features, user content
          features, AI providers, uploads, storage, sharing, payments, public publishing, or other
          behavior that affects user rights or responsibilities. The effective date above identifies
          the current published version.
        </p>
      </Section>
    </LegalShell>
  );
}
